import {
  ApplicationRef,
  ComponentRef,
  Directive,
  ElementRef,
  EmbeddedViewRef,
  HostListener,
  Input,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { TooltipComponent } from './tooltip.component';

@Directive({
  selector: '[tooltip]',
  standalone: true,
})
export class TooltipDirective {
  @Input() tooltip: string = '';
  /** Provide a real Angular template for interactive tooltips (click handlers, components, etc.). */
  @Input() tooltipTemplate: TemplateRef<any> | null = null;
  @Input() maxWidth: string = 'none';
  @Input() minWidth: string = 'auto';
  @Input() offsetLeft: number = 0;
  @Input() offsetTop: number = 0;
  /** When true, center the tooltip horizontally on the trigger and place it just below (flips above near the viewport edge). */
  @Input() center: boolean = false;
  /** Small delay to allow moving the cursor from trigger -> tooltip without it disappearing */
  @Input() hideDelayMs: number = 200;

  content: string = '';
  private componentRef: ComponentRef<TooltipComponent> | null = null;
  private hideTimeoutId: number | null = null;
  private tooltipMouseEnterHandler: (() => void) | null = null;
  private tooltipMouseLeaveHandler: (() => void) | null = null;

  constructor(
    private elementRef: ElementRef,
    private appRef: ApplicationRef,
    private viewContainerRef: ViewContainerRef
  ) {}

  private clearHideTimer(): void {
    if (this.hideTimeoutId !== null) {
      window.clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = null;
    }
  }

  private scheduleHide(): void {
    this.clearHideTimer();
    this.hideTimeoutId = window.setTimeout(() => {
      if (this.componentRef !== null) {
        this.componentRef.instance.visible = false;
      }
      this.destroy();
    }, this.hideDelayMs);
  }

  private getTooltipElement(): HTMLElement | null {
    if (this.componentRef === null) return null;
    const domElem = (this.componentRef.hostView as EmbeddedViewRef<any>)
      .rootNodes[0] as HTMLElement;
    // Root node is usually the tooltip <div>, but keep querySelector for safety
    return (domElem?.classList?.contains('tooltip')
      ? domElem
      : (domElem?.querySelector?.('.tooltip') as HTMLElement)) || null;
  }

  private attachTooltipHoverHandlers(): void {
    const tooltipElem = this.getTooltipElement();
    if (!tooltipElem) return;

    // Avoid attaching multiple times
    if (this.tooltipMouseEnterHandler || this.tooltipMouseLeaveHandler) return;

    this.tooltipMouseEnterHandler = () => this.clearHideTimer();
    this.tooltipMouseLeaveHandler = () => this.scheduleHide();

    tooltipElem.addEventListener('mouseenter', this.tooltipMouseEnterHandler);
    tooltipElem.addEventListener('mouseleave', this.tooltipMouseLeaveHandler);
  }

  private detachTooltipHoverHandlers(): void {
    const tooltipElem = this.getTooltipElement();
    if (!tooltipElem) return;
    if (this.tooltipMouseEnterHandler) {
      tooltipElem.removeEventListener('mouseenter', this.tooltipMouseEnterHandler);
      this.tooltipMouseEnterHandler = null;
    }
    if (this.tooltipMouseLeaveHandler) {
      tooltipElem.removeEventListener('mouseleave', this.tooltipMouseLeaveHandler);
      this.tooltipMouseLeaveHandler = null;
    }
  }

  /**
   * The tooltip is appended inside the trigger and is position:fixed. If any ancestor establishes
   * a containing block for fixed elements (a transform/perspective/filter/will-change/contain — e.g.
   * a slide-in dialog panel), `left/top` resolve against that ancestor instead of the viewport.
   * Returns that ancestor's viewport offset so centered tooltips can compensate; {0,0} when none.
   */
  private getFixedContainingBlockOffset(): { left: number; top: number } {
    let el = this.elementRef.nativeElement as HTMLElement | null;
    while (el && el !== document.body && el !== document.documentElement) {
      const s = window.getComputedStyle(el);
      const establishesContainingBlock =
        (s.transform && s.transform !== 'none') ||
        (s.perspective && s.perspective !== 'none') ||
        (s.filter && s.filter !== 'none') ||
        (s.willChange && s.willChange.indexOf('transform') !== -1) ||
        (s.contain && /(paint|layout|strict|content)/.test(s.contain));
      if (establishesContainingBlock) {
        const r = el.getBoundingClientRect();
        return { left: r.left, top: r.top };
      }
      el = el.parentElement;
    }
    return { left: 0, top: 0 };
  }

  private setPosition(): void {
    if (this.componentRef !== null) {
      const trigger = this.elementRef.nativeElement;
      const {
        left: triggerLeft,
        top: triggerTop,
        height: triggerHeight,
        width: triggerWidth,
      } = trigger.getBoundingClientRect();

      // Get tooltip element from the appended DOM
      const domElem = (this.componentRef.hostView as EmbeddedViewRef<any>)
        .rootNodes[0] as HTMLElement;
      const tooltipElem = domElem?.querySelector('.tooltip') as HTMLElement;
      
      if (!tooltipElem) {
        // If element not ready, retry on next frame
        requestAnimationFrame(() => {
          setTimeout(() => this.setPosition(), 0);
        });
        return;
      }

      // Update dimensions if not set
      if (this.componentRef.instance.width === 0 || this.componentRef.instance.height === 0) {
        this.componentRef.instance.width = tooltipElem.offsetWidth;
        this.componentRef.instance.height = tooltipElem.offsetHeight;
      }

      let width;
      if(this.minWidth !== 'auto')
      {
        width = parseFloat(this.minWidth.replace('px', ''));
      }
      else if(this.maxWidth !== 'none'){
        width = parseFloat(this.maxWidth.replace('px', ''));
      }
      else {
        width = this.componentRef.instance.width || tooltipElem.offsetWidth;
      }

      const tooltipHeight = this.componentRef.instance.height || tooltipElem.offsetHeight;
      const tooltipWidth =
        this.maxWidth === 'none'
          ? (this.componentRef.instance.width || tooltipElem.offsetWidth)
          : parseFloat(this.maxWidth.replace('px', ''));
          this.minWidth = `${width}px`;

      let _left = triggerLeft + triggerWidth - 5 - this.offsetLeft;
      let _top = triggerTop + triggerHeight - 10 - this.offsetTop;
      const { height: bodyHeight, width: bodyWidth } =
        document.body.getBoundingClientRect();

      if (this.center) {
        // Center horizontally on the trigger, sit just below it (offsets still nudge if provided).
        // Use `width` (the value forced onto the tooltip's min-width below) rather than the
        // pre-layout offsetWidth — otherwise we center for the natural width while the tooltip
        // renders at the forced width, leaving it visibly off-center.
        const actualWidth = width || tooltipElem.offsetWidth;
        // Resolve the position in viewport space first.
        let vLeft = triggerLeft + triggerWidth / 2 - actualWidth / 2 - this.offsetLeft;
        let vTop = triggerTop + triggerHeight + 8 - this.offsetTop;
        // Flip above when it would overflow the viewport bottom.
        if (vTop + tooltipHeight > bodyHeight - 5) {
          vTop = triggerTop - tooltipHeight - 8 - this.offsetTop;
        }
        // Keep within the horizontal viewport bounds.
        if (vLeft < 5) vLeft = 5;
        if (vLeft + actualWidth > bodyWidth - 5) vLeft = bodyWidth - actualWidth - 5;
        // A transformed ancestor (e.g. the slide-in dialog panel) becomes the containing block
        // for the position:fixed tooltip, so convert the viewport target into that space.
        const cb = this.getFixedContainingBlockOffset();
        _left = vLeft - cb.left;
        _top = vTop - cb.top;
      } else {
        if (triggerTop + tooltipHeight > bodyHeight - 5) {
          _top = triggerTop - tooltipHeight;
        }
        if (triggerLeft + tooltipWidth > bodyWidth - 5) {
          _left = triggerLeft - tooltipWidth;
        }
      }
      this.componentRef.instance.left = _left;
      this.componentRef.instance.top = _top;
      this.componentRef.instance.minWidth = this.minWidth;
      this.componentRef.instance.maxWidth = this.maxWidth;
      // Make tooltip visible after position is set
      this.componentRef.instance.visible = true;

      // Now that tooltip exists and is visible, allow hovering it to keep it open
      this.attachTooltipHoverHandlers();
    }
  }

  private setTooltipComponentProperties(): void {
    if (this.componentRef !== null) {
      this.componentRef.instance.template = this.tooltipTemplate;
      this.componentRef.instance.tooltip = this.content;
      // Use requestAnimationFrame to ensure DOM is ready before calculating position
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.setPosition();
        }, 0);
      });
    }
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.clearHideTimer();
    if (this.componentRef === null) {
      this.componentRef =
        this.viewContainerRef.createComponent(TooltipComponent);
      if (this.tooltipTemplate) {
        this.content = '';
      } else {
        const htmlContent = this.elementRef.nativeElement.querySelector(
          this.tooltip
        );
        this.content = htmlContent?.outerHTML || '';
      }
      const domElem = (this.componentRef.hostView as EmbeddedViewRef<any>)
        .rootNodes[0] as HTMLElement;
        this.elementRef.nativeElement.appendChild(domElem);
      this.setTooltipComponentProperties();
    } else {
      // Tooltip already exists (e.g. re-enter during hide delay) — ensure it stays visible
      this.componentRef.instance.visible = true;
      this.attachTooltipHoverHandlers();
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    // Don't destroy immediately; give time to move cursor into the tooltip itself.
    this.scheduleHide();
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  private destroy(): void {
    if (this.componentRef !== null) {
      this.clearHideTimer();
      this.detachTooltipHoverHandlers();
      this.appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
      this.componentRef = null;
    }
  }
}
